/**
 * Teams Authentication Context
 *
 * Provides authentication for Teams apps using SSO (Single Sign-On).
 * Falls back to MSAL authentication if Teams SSO is unavailable.
 * Maintains the same interface as AuthContext for compatibility.
 */

import React, { createContext, useEffect, useState, useMemo } from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import { useTeams } from '@/hooks/useTeams';
import { apiConfig } from '@/config/authConfig';
import axios from 'axios';

interface TeamsAuthContextType {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  isLoading: boolean;
  error: Error | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export const TeamsAuthContext = createContext<TeamsAuthContextType | undefined>(
  undefined
);

/**
 * Parse JWT token to extract user information
 * @param token - JWT token string
 * @returns Partial AccountInfo with user details
 */
function parseJwtToken(token: string): Partial<AccountInfo> {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const payload = JSON.parse(jsonPayload) as {
      oid?: string;
      upn?: string;
      name?: string;
      preferred_username?: string;
      tid?: string;
    };

    return {
      homeAccountId: payload.oid || '',
      environment: 'login.windows.net',
      tenantId: payload.tid || '',
      username: payload.upn || payload.preferred_username || '',
      localAccountId: payload.oid || '',
      name: payload.name || payload.preferred_username || '',
    };
  } catch (err) {
    console.error('Error parsing JWT token:', err);
    return {};
  }
}

export const TeamsAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const teamsContext = useTeams();

  // Handle null return when hook is called outside TeamsProvider
  const isInTeams = teamsContext?.isInTeams ?? false;
  const isInitialized = teamsContext?.isInitialized ?? false;
  const getTeamsAuthToken = useMemo(
    () =>
      teamsContext?.getAuthToken ??
      (async () => {
        throw new Error('Teams context not available');
      }),
    [teamsContext]
  );
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      if (!isInTeams || !isInitialized) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Get Teams SSO token
        const token = await getTeamsAuthToken();

        // Exchange Teams token for backend access token
        const backendToken = await exchangeTokenForBackendAccess(token);

        // Parse user info from the token
        const userInfo = parseJwtToken(token);

        setAccessToken(backendToken);
        setUser(userInfo as AccountInfo);
        setIsAuthenticated(true);
      } catch (err) {
        console.error('Error during Teams SSO authentication:', err);
        setError(err as Error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [isInTeams, isInitialized, getTeamsAuthToken]);

  /**
   * Exchange Teams SSO token for backend access token
   * This function would call your backend API to exchange the token
   */
  const exchangeTokenForBackendAccess = async (
    teamsToken: string
  ): Promise<string> => {
    try {
      // Call backend API to exchange Teams token for access token
      // This is a placeholder - implement according to your backend API
      const response = await axios.post<{ accessToken: string }>(
        `${apiConfig.baseUrl}/auth/exchange-teams-token`,
        { teamsToken },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data.accessToken;
    } catch (err) {
      console.error('Error exchanging Teams token:', err);
      // For now, return the Teams token directly
      // In production, you should implement proper token exchange
      return teamsToken;
    }
  };

  /**
   * Login is automatic in Teams via SSO
   * This method is a no-op for compatibility with AuthContext interface
   */
  const login = async (): Promise<void> => {
    if (isAuthenticated) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const token = await getTeamsAuthToken();
      const backendToken = await exchangeTokenForBackendAccess(token);
      const userInfo = parseJwtToken(token);

      setAccessToken(backendToken);
      setUser(userInfo as AccountInfo);
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Login error:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout - in Teams context, this just clears local state
   * The user remains signed in to Teams
   */
  const logout = async (): Promise<void> => {
    setIsLoading(true);

    try {
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
    } catch (err) {
      console.error('Logout error:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get access token for API calls
   * Returns cached token or requests a new one
   */
  const getAccessToken = async (): Promise<string | null> => {
    if (accessToken) {
      return accessToken;
    }

    try {
      const token = await getTeamsAuthToken();
      const backendToken = await exchangeTokenForBackendAccess(token);
      setAccessToken(backendToken);
      return backendToken;
    } catch (err) {
      console.error('Error getting access token:', err);
      setError(err as Error);
      return null;
    }
  };

  const value: TeamsAuthContextType = {
    isAuthenticated,
    user,
    login,
    logout,
    getAccessToken,
    isLoading,
    error,
  };

  return (
    <TeamsAuthContext.Provider value={value}>
      {children}
    </TeamsAuthContext.Provider>
  );
};
